import { ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { TYPOGRAPHY_VARIANTS } from '@backrs_ui/common-ui/src/designSystem/typography/typography';

import { SxProps, Theme } from '@mui/material';

import {
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_COLLAPSED_WIDTH_WITH_SCROLLBAR,
  SIDEBAR_EMULATION_MARGIN,
  SIDEBAR_EXPANDED_WIDTH,
} from './sidebar.constants';

export const paperSx =
  (isOpen: boolean, isEmulating: boolean): SxProps<Theme> =>
  ({ backrs, zIndex }) => ({
    width: isOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH,
    overflowX: 'hidden',
    boxSizing: 'border-box',
    backgroundColor: backrs.colors['Beige/50'],
    transition: 'width 0.3s ease-in-out',
    zIndex: zIndex.drawer,
    ...(isEmulating
      ? {
          overflow: 'hidden',
          border: `2px solid ${backrs.colors['Colors/Foreground/fg-success-secondary']}`,
          borderRadius: backrs.radius['radius-lg'],
          margin: SIDEBAR_EMULATION_MARGIN,
          height: `calc(100dvh - ${SIDEBAR_EMULATION_MARGIN} * 2)`,
        }
      : {
          borderRight: `1px solid ${backrs.colors['Colors/Base/Black']}`,
        }),
    '@media screen and (min-width: 768px) and (max-width: 1366px) and (max-height: 700px)': {
      width: isOpen ? SIDEBAR_EXPANDED_WIDTH : SIDEBAR_COLLAPSED_WIDTH_WITH_SCROLLBAR,
    },
  });

export const paperInnerScrollSx =
  (fillsParent: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: fillsParent ? '100%' : '100dvh',
    justifyContent: 'space-between',
    overflow: 'hidden',
    backgroundColor: backrs.colors['Beige/50'],
  });

export const drawerContentContainerSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    paddingTop: isOpen
      ? { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-2xl'] }
      : { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-xl'] },
    paddingBottom: backrs.spacing['spacing-lg'],
    paddingLeft: isOpen
      ? {
          xs: backrs.containerSpacing['container-padding-mobile'],
          sm: backrs.spacing['spacing-2xl'],
        }
      : backrs.spacing['spacing-lg'],
    paddingRight: isOpen ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-lg'],
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: backrs.colors['Beige/50'],
    transition: 'all 0.3s ease-in-out',
  });

export const profileWidgetContainerSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    paddingLeft: isOpen ? backrs.spacing['spacing-2xl'] : backrs.spacing['spacing-lg'],
    paddingRight: isOpen ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-lg'],
    paddingBottom: backrs.spacing['spacing-2xl'],

    backgroundColor: backrs.colors['Beige/50'],
  });

export const logoContainerSx: SxProps<Theme> = ({ backrs }) => ({
  gap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-lg'] },
  display: 'flex',
  alignItems: 'center',
  transition: 'all 0.3s ease-in-out',
  marginBottom: backrs.spacing['spacing-2xl'],
});

export const backrsLogoImageSx =
  (isOpen: boolean): SxProps<Theme> =>
  () => ({
    cursor: 'pointer',
    height: isOpen
      ? { xs: ICON_SIZES['32px'], sm: ICON_SIZES['32px'] }
      : { xs: ICON_SIZES['32px'], sm: ICON_SIZES['48px'] },
    width: isOpen
      ? { xs: ICON_SIZES['32px'], sm: ICON_SIZES['32px'] }
      : { xs: ICON_SIZES['32px'], sm: ICON_SIZES['48px'] },
    transition: 'height 0.3s, width 0.3s',
  });

export const logoTextContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: { xs: 'unset', sm: '100%' },

  gap: { xs: backrs.spacing['spacing-lg'], sm: backrs.spacing['spacing-3xl'] },
});

export const openChevronIconSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    opacity: isOpen ? 0 : 1,
    height: isOpen ? '0px' : '28px',
    width: isOpen ? '0px' : '28px',
    minHeight: isOpen ? '0px' : '28px',
    minWidth: isOpen ? '0px' : '28px',
    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'all 0.3s ease-in-out',
    marginBottom: isOpen ? backrs.spacing['spacing-none'] : backrs.spacing['spacing-2xl'],
    display: { xs: 'none', sm: 'flex' },
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    justifySelf: 'center',
    borderRadius: backrs.radius['radius-full'],
    color: backrs.colors['Colors/Text/text-quaternary'],
    '&:hover': {
      backgroundColor: backrs.colors['GrayTrue/200'],
    },
  });

export const searchBarSx =
  (isOpen: boolean, isPageActive: boolean): SxProps<Theme> =>
  ({ backrs }) => {
    const collapsedSidebarBorder = isPageActive
      ? backrs.colors['Colors/Base/Black']
      : backrs.colors['Colors/Base/Transparent'];
    const expandedSidebarBorder = backrs.colors['Gray/300'];
    const searchBarBorder = isOpen ? expandedSidebarBorder : collapsedSidebarBorder;

    return {
      mb: isOpen ? backrs.spacing['spacing-xl'] : backrs.spacing['spacing-none'],
      display: 'flex',
      justifyContent: 'center',
      transition: 'all 0.3s ease-in-out',
      borderRadius: backrs.radius['radius-sm'],
      width: isOpen ? '100%' : 'auto',
      minWidth: backrs.spacing['spacing-5xl'],
      minHeight: backrs.spacing['spacing-5xl'],
      maxWidth: !isOpen ? '48px' : 'unset',

      '& .MuiOutlinedInput-root': {
        maxWidth: isOpen ? { xs: '100%', sm: SIDEBAR_EXPANDED_WIDTH } : '48px',
        minWidth: '48px',
        paddingY: backrs.spacing['spacing-md'],
        paddingX: backrs.spacing['spacing-lg'],
        borderRadius: backrs.radius['radius-md'],
        border: `1px solid ${searchBarBorder}`,
        backgroundColor:
          !isOpen && isPageActive ? backrs.colors['Colors/Background/bg-primary'] : 'transparent',
        transition: `max-width 0.3s ease-in-out, background-color 0.3s ease-in-out, border 0.3s ease-in-out`,
        gap: backrs.spacing['spacing-lg'],
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          border: '1px solid transparent !important',
        },
        cursor: isOpen ? 'default' : 'pointer',
        '&.Mui-focused': {
          ...(isOpen
            ? { borderColor: backrs.colors['Colors/Border/border-brand'] }
            : {
                boxShadow: `0 0 0 1px transparent !important`,
                border: `1px solid ${searchBarBorder} !important`,
              }),
        },
        '&:active': {
          transition: 'all 0.3s ease-in-out',
          border: `1px solid ${backrs.colors['Colors/Border/border-brand']}`,
        },
      },

      '&:hover': !isOpen
        ? {
            backgroundColor: backrs.colors['Colors/Background/bg-primary'],
            transform: 'translate(-2px, -2px)',
            boxShadow: '4px 4px 0px 0px rgba(0, 0, 0, 1)',
          }
        : {},

      '&:active': {
        transform: 'none !important',
        boxShadow: 'none !important',
        transition: 'all 0s !important',
      },

      '&:hover .MuiOutlinedInput-root':
        !isPageActive && !isOpen
          ? { border: `1px solid ${backrs.colors['Colors/Base/Black']}` }
          : {},

      '&:active .MuiOutlinedInput-root': {
        boxShadow: 'none !important',
        transition: 'all 0s !important',
        transform: 'none !important',
      },

      '& .MuiOutlinedInput-input': {
        color: backrs.colors['Colors/Text/text-primary'],
        padding: backrs.spacing['spacing-none'],
        fontWeight: TYPOGRAPHY_VARIANTS['text-md-regular'].fontWeight,
        fontSize: TYPOGRAPHY_VARIANTS['text-md-regular'].fontSize,
        display: 'flex',
        border: 'none',
        '&::placeholder': {
          color: isOpen ? backrs.colors['Colors/Text/text-placeholder'] : 'transparent',
          transition: 'all 0.3s ease-in-out',
          opacity: 1,
        },
      },

      '& input:-webkit-autofill': {
        transition: 'background-color 600000s 0s, color 600000s 0s',
      },
      '& .MuiInputLabel-outlined': {
        color: backrs.colors['Colors/Text/text-primary'],
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    };
  };

export const expandedSearchIconAdornmentSx =
  (isOpen: boolean): SxProps<Theme> =>
  () => ({
    minWidth: ICON_SIZES['20px'],
    minHeight: ICON_SIZES['20px'],
    display: 'flex',
    alignItems: 'center',
    cursor: isOpen ? 'default' : 'pointer',
  });

export const sidebarSectionSx: SxProps<Theme> = ({ backrs }) => ({
  gap: backrs.spacing['spacing-sm'],
  display: 'flex',
  flexDirection: 'column',
});

export const sidebarSectionsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-xl'],
});

export const sectionBottomBorderSx: SxProps<Theme> = ({ backrs }) => ({
  borderBottom: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  my: backrs.spacing['spacing-xs'],
  mx: backrs.spacing['spacing-md'],
});

export const mobileCloseBtnContainerSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'absolute',
  top: '0px',
  right: '0px',
  zIndex: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: backrs.spacing['spacing-lg'],
  paddingRight: backrs.spacing['spacing-lg'],
  paddingLeft: backrs.spacing['spacing-lg'],

  opacity: 0.7,
});

export const mobileDrawerBackdropSx: SxProps<Theme> = () => ({
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
});

export const mobileDrawerPaperSx: SxProps<Theme> = () => ({
  position: 'static',
  maxWidth: '82vw',
});
